import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DashboardAnimation = () => {
  const [tests, setTests] = useState([
    { id: 1, status: 'pending', name: 'Data Freshness' },
    { id: 2, status: 'pending', name: 'Schema Validation' },
    { id: 3, status: 'pending', name: 'Completeness Check' },
    { id: 4, status: 'pending', name: 'Duplicates Records' },
    { id: 5, status: 'pending', name: 'Anomaly Detection' },
    { id: 6, status: 'pending', name: 'Data Quality Score' }
  ]);
  
  const [currentTest, setCurrentTest] = useState(null);
  const [statusMessage, setStatusMessage] = useState('Initializing tests...');
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    // Set mounted flag to true after component is mounted
    setIsMounted(true);
    return () => setIsMounted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusColors = {
    pending: '#4B5563',
    running: '#4F46E5',
    passed: '#10B981',
    failed: '#EF4444',
    warning: '#F59E0B'
  };

  useEffect(() => {
    if (!isMounted) return;
    
    const runTests = async () => {
      try {
        const results = ['passed', 'warning', 'passed', 'passed', 'warning', 'failed'];
        
        // Initial delay before starting tests
        await new Promise(resolve => setTimeout(resolve, 1000));
        if (!isMounted) return;
        setStatusMessage('Starting data quality assessment...');
        
        for (let i = 0; i < tests.length; i++) {
          if (!isMounted) return;
          // Update status message and current test
          setCurrentTest(tests[i]);
          setStatusMessage(`Checking for ${tests[i].name.toLowerCase()}...`);
          
          // First set status to running
          setTests(prev => prev.map((test, index) => 
            index === i ? { ...test, status: 'running' } : test
          ));

          // Wait for test to complete (1s)
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!isMounted) return;

          // Set the final status
          setTests(prev => prev.map((test, index) => 
            index === i ? { ...test, status: results[i] } : test
          ));
          
          // Update status message with result
          const resultText = results[i] === 'passed' ? 'passed' : 
                          results[i] === 'warning' ? 'passed with warnings' : 'failed';
          setStatusMessage(`${tests[i].name} check ${resultText}.`);
          
          // Pause between tests
          await new Promise(resolve => setTimeout(resolve, 1000));
          if (!isMounted) return;
        }

        // Tests complete message
        setStatusMessage('Data quality assessment complete.');
        setCurrentTest(null);
        
        // Reset after 3 seconds and start again
        setTimeout(() => {
          if (!isMounted) return;
          setTests(prev => prev.map(test => ({ ...test, status: 'pending' })));
          runTests();
        }, 3000);
      } catch (error) {
        console.error("Animation error:", error);
      }
    };

    runTests();
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted]);

  return (
    <div className="bg-[#1A1B23] rounded-xl overflow-hidden shadow-2xl w-full">
      <div className="bg-white rounded-t-xl p-2 flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-[#FF5F57]"></div>
        <div className="w-3 h-3 rounded-full bg-[#FFBD2E]"></div>
        <div className="w-3 h-3 rounded-full bg-[#28C840]"></div>
        <span className="ml-2 text-gray-500 text-sm">Qupid Dashboard</span>
      </div>
      
      <div className="bg-[#1A1B23] p-4 sm:p-6">
        <h2 className="text-white text-lg sm:text-xl mb-2">Data Quality Dashboard</h2>
        
        {/* Status message display */}
        <div className="mb-4 h-8 flex items-center overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.p 
              key={statusMessage}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              transition={{ duration: 0.2 }}
              className="text-gray-400 text-sm whitespace-nowrap overflow-hidden text-ellipsis"
            >
              {statusMessage}
              {currentTest && currentTest.status === 'running' && (
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ repeat: Infinity, duration: 1.2 }}
                  className="inline-block ml-1"
                >
                  ⋯
                </motion.span>
              )}
            </motion.p>
          </AnimatePresence>
        </div>
        
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {tests.map((test) => (
            <motion.div
              key={test.id}
              className="bg-[#2A2B32] rounded-lg p-3 sm:p-4 h-20 sm:h-24 flex flex-col justify-between"
              initial={{ opacity: 0.6 }}
              animate={{
                opacity: 1,
                scale: test.status === 'running' ? 1.02 : 1,
                border: test.status === 'running' ? '1px solid #4F46E5' : '1px solid transparent',
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center justify-between w-full">
                <span className="text-gray-400 text-xs sm:text-sm">{test.name}</span>
                <div className="flex-shrink-0 w-3 h-3 sm:w-4 sm:h-4 relative">
                  <AnimatePresence mode='wait'>
                    <motion.div
                      key={test.status}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ 
                        scale: 1, 
                        opacity: 1,
                        ...(test.status === 'running' ? {
                          boxShadow: ['0 0 0 rgba(79, 70, 229, 0)', '0 0 10px rgba(79, 70, 229, 0.7)', '0 0 0 rgba(79, 70, 229, 0)']
                        } : {})
                      }}
                      exit={{ scale: 0.5, opacity: 0 }}
                      transition={{ 
                        duration: 0.3,
                        ...(test.status === 'running' ? {
                          boxShadow: {
                            repeat: Infinity,
                            duration: 1.5,
                          }
                        } : {})
                      }}
                      className="absolute inset-0 rounded-full"
                      style={{ backgroundColor: statusColors[test.status] }}
                    />
                  </AnimatePresence>
                </div>
              </div>
              
              <div className="w-full">
                {test.status === 'running' ? (
                  <motion.div
                    className="w-full bg-gray-700 h-1 rounded-full overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <motion.div
                      className="h-full bg-indigo-600"
                      animate={{
                        width: ["0%", "100%"],
                      }}
                      transition={{
                        duration: 0.8,
                        ease: "easeInOut",
                      }}
                    />
                  </motion.div>
                ) : (
                  <div className="w-full h-1" /> 
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardAnimation; 